import { useEffect, useState } from 'react';
import { Plus, X, Trash, FloppyDisk, CheckSquare, ShieldCheck, Files, Copy, DotsSixVertical, Stack, Star } from '@phosphor-icons/react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCatalogs } from '@/hooks/useTasks';
import {
  usePlantillas,
  usePlantilla,
  useCreatePlantilla,
  useUpdatePlantilla,
  useDeletePlantilla,
  useDuplicatePlantilla,
  useCreatePlantillaItem,
  useUpdatePlantillaItem,
  useDeletePlantillaItem,
  useReorderPlantillaItems,
} from '@/hooks/usePlantillas';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { SegmentedControl } from '@/components/common/SegmentedControl';
import type { PlantillaTipo, PlantillaTarea, PlantillaTareaItem } from '@/types';

type TipoFilter = 'all' | PlantillaTipo;

export function PlantillasPage() {
  const { data: catalogs } = useCatalogs();
  const [filterCat, setFilterCat] = useState<number | null>(null);
  const [filterTipo, setFilterTipo] = useState<TipoFilter>('all');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showNew, setShowNew] = useState(false);

  const { data: plantillas = [], isLoading } = usePlantillas({
    categoryId: filterCat ?? undefined,
    tipo: filterTipo === 'all' ? undefined : filterTipo,
  });

  const { data: detail } = usePlantilla(selectedId);

  // Si la plantilla seleccionada cae fuera del filtro actual, deseleccionar.
  useEffect(() => {
    if (selectedId !== null && !plantillas.some(p => p.idPlantillaTarea === selectedId)) {
      setSelectedId(null);
    }
  }, [plantillas, selectedId]);

  const categoryName = (id: number) =>
    catalogs?.categories.find(c => c.idCatTareaArq === id)?.nombre ?? `#${id}`;

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      {/* Toolbar */}
      <div className="px-4 py-3 border-b border-stone-200 bg-white flex items-center gap-4 flex-wrap">
        <h1 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
          <Files size={20} weight="bold" />
          Plantillas
        </h1>

        <div className="flex items-center gap-2 ml-2">
          <label className="text-xs font-medium text-stone-600">Categoría</label>
          <div className="w-56">
            <SearchableSelect<number>
              options={(catalogs?.categories ?? []).map(c => ({
                value: c.idCatTareaArq,
                label: c.nombre || `#${c.idCatTareaArq}`,
              }))}
              value={filterCat}
              onChange={setFilterCat}
              placeholder="— Todas —"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-stone-600">Tipo</label>
          <SegmentedControl<TipoFilter>
            options={[
              { value: 'all', label: 'Todos' },
              { value: 'checklist', label: 'Checklist', icon: <CheckSquare size={12} weight="bold" /> },
              { value: 'revision', label: 'Revisión', icon: <ShieldCheck size={12} weight="bold" /> },
              { value: 'ambas', label: 'Ambas', icon: <Stack size={12} weight="bold" /> },
            ]}
            value={filterTipo}
            onChange={setFilterTipo}
          />
        </div>

        <button
          onClick={() => setShowNew(true)}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-black bg-brand-green-100 hover:bg-brand-green-200 rounded"
        >
          <Plus size={14} weight="bold" /> Nueva plantilla
        </button>
      </div>

      {/* Master-detail */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-[320px_1fr] overflow-hidden">
        {/* Lista */}
        <aside className="border-r border-stone-200 bg-white overflow-y-auto">
          {isLoading && (
            <div className="px-4 py-6 text-sm text-stone-500">Cargando…</div>
          )}
          {!isLoading && plantillas.length === 0 && (
            <div className="px-4 py-6 text-sm text-stone-500 italic">
              No hay plantillas con esos filtros. Creá una con el botón de arriba.
            </div>
          )}
          {plantillas.map(p => {
            const isActive = p.idPlantillaTarea === selectedId;
            return (
              <button
                key={p.idPlantillaTarea}
                type="button"
                onClick={() => setSelectedId(p.idPlantillaTarea)}
                className={`w-full text-left px-4 py-3 border-b border-stone-100 transition-colors ${
                  isActive ? 'bg-brand-green-100/20 border-l-4 border-l-brand-green-100' : 'hover:bg-stone-50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm ${isActive ? 'font-semibold text-stone-900' : 'text-stone-800'} truncate flex items-center gap-1.5`}>
                    {p.esPredeterminada && (
                      <Star
                        size={12}
                        weight="fill"
                        className="text-brand-yellow flex-shrink-0"
                      />
                    )}
                    {p.nombre}
                  </span>
                  {!p.activo && (
                    <span className="text-[10px] font-medium text-stone-500 bg-stone-100 rounded px-1.5 py-0.5">
                      Inactivo
                    </span>
                  )}
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px] text-stone-500">
                  <span className="flex items-center gap-1.5">
                    {p.tipo === 'revision' ? (
                      <ShieldCheck size={11} weight="bold" />
                    ) : p.tipo === 'ambas' ? (
                      <Stack size={11} weight="bold" />
                    ) : (
                      <CheckSquare size={11} weight="bold" />
                    )}
                    {p.tipo === 'revision' ? 'Revisión' : p.tipo === 'ambas' ? 'Ambas' : 'Checklist'}
                    <span className="text-stone-400">·</span>
                    <span>{categoryName(p.idCategoria)}</span>
                  </span>
                  <span>{p.itemCount} items</span>
                </div>
              </button>
            );
          })}
        </aside>

        {/* Detalle */}
        <section className="overflow-y-auto p-6">
          {detail ? (
            <PlantillaDetail
              plantilla={detail}
              onDeleted={() => setSelectedId(null)}
              onDuplicated={(newId) => setSelectedId(newId)}
            />
          ) : (
            <div className="text-sm text-stone-500 italic flex items-center gap-2">
              <Files size={16} weight="bold" />
              Seleccioná una plantilla para ver y editar sus items.
            </div>
          )}
        </section>
      </div>

      {showNew && (
        <NewPlantillaDialog
          defaultCategoryId={filterCat}
          // Si el filtro de la pagina ya restringe a un tipo, arrancamos ahi;
          // 'all' cae a 'checklist' (el caso mas comun).
          defaultTipo={filterTipo === 'all' ? 'checklist' : filterTipo}
          onClose={() => setShowNew(false)}
          onCreated={(id) => { setSelectedId(id); setShowNew(false); }}
        />
      )}
    </div>
  );
}

// ============================================================
// PlantillaDetail - panel derecho con cabecera editable + items.
// El nombre/categoria/tipo/activo se persisten al perder foco
// (blur) o al hacer Enter. Items son inline editables tambien.
// ============================================================
function PlantillaDetail({
  plantilla,
  onDeleted,
  onDuplicated,
}: {
  plantilla: PlantillaTarea;
  onDeleted: () => void;
  onDuplicated: (newId: number) => void;
}) {
  const { data: catalogs } = useCatalogs();
  const updatePlantilla = useUpdatePlantilla();
  const deletePlantilla = useDeletePlantilla();
  const duplicatePlantilla = useDuplicatePlantilla();
  const createItem = useCreatePlantillaItem();
  const reorderItems = useReorderPlantillaItems();

  const [nombre, setNombre] = useState(plantilla.nombre);
  const [idCategoria, setIdCategoria] = useState<number | null>(plantilla.idCategoria);
  const [tipo, setTipo] = useState<PlantillaTipo>(plantilla.tipo);
  const [activo, setActivo] = useState(plantilla.activo);
  const [esPredeterminada, setEsPredeterminada] = useState(plantilla.esPredeterminada);

  // Re-sincronizar cuando cambia la plantilla seleccionada.
  useEffect(() => {
    setNombre(plantilla.nombre);
    setIdCategoria(plantilla.idCategoria);
    setTipo(plantilla.tipo);
    setActivo(plantilla.activo);
    setEsPredeterminada(plantilla.esPredeterminada);
  }, [plantilla.idPlantillaTarea, plantilla.nombre, plantilla.idCategoria, plantilla.tipo, plantilla.activo, plantilla.esPredeterminada]);

  const headerDirty =
    nombre !== plantilla.nombre ||
    idCategoria !== plantilla.idCategoria ||
    tipo !== plantilla.tipo ||
    activo !== plantilla.activo ||
    esPredeterminada !== plantilla.esPredeterminada;

  function saveHeader() {
    if (!headerDirty) return;
    if (!nombre.trim()) return;
    if (idCategoria === null) return;
    updatePlantilla.mutate({
      id: plantilla.idPlantillaTarea,
      dto: { nombre: nombre.trim(), idCategoria, tipo, activo, esPredeterminada },
    });
  }

  async function handleDelete() {
    if (!window.confirm(`¿Eliminar la plantilla "${plantilla.nombre}"? Se borran también todos sus items.`)) return;
    await deletePlantilla.mutateAsync(plantilla.idPlantillaTarea);
    onDeleted();
  }

  async function handleDuplicate() {
    const copy = await duplicatePlantilla.mutateAsync(plantilla.idPlantillaTarea);
    onDuplicated(copy.idPlantillaTarea);
  }

  async function handleAddItem() {
    const text = window.prompt('Texto del item');
    if (!text || !text.trim()) return;
    await createItem.mutateAsync({
      plantillaId: plantilla.idPlantillaTarea,
      dto: { texto: text.trim() },
    });
  }

  // Local state que mantiene el orden optimisticamente durante el drag.
  // Se sincroniza cuando llega data fresca del backend (e.g. tras un
  // refetch o si otro usuario reordena).
  const [localItems, setLocalItems] = useState<PlantillaTareaItem[]>(
    () => [...plantilla.items].sort((a, b) => a.orden - b.orden)
  );
  useEffect(() => {
    setLocalItems([...plantilla.items].sort((a, b) => a.orden - b.orden));
  }, [plantilla.items]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = localItems.findIndex(i => i.idPlantillaTareaItem === active.id);
    const newIndex = localItems.findIndex(i => i.idPlantillaTareaItem === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(localItems, oldIndex, newIndex);
    setLocalItems(next);
    reorderItems.mutate({
      plantillaId: plantilla.idPlantillaTarea,
      orderedIds: next.map(i => i.idPlantillaTareaItem),
    });
  }

  return (
    <div className="max-w-3xl space-y-5">
      {/* Cabecera */}
      <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Nombre</label>
          <input
            value={nombre}
            onChange={e => setNombre(e.target.value)}
            onBlur={saveHeader}
            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Categoría</label>
            <SearchableSelect<number>
              options={(catalogs?.categories ?? []).map(c => ({
                value: c.idCatTareaArq,
                label: c.nombre || `#${c.idCatTareaArq}`,
              }))}
              value={idCategoria}
              onChange={(v) => { setIdCategoria(v); }}
              placeholder="— Seleccionar —"
              clearable={false}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Tipo</label>
            <SegmentedControl<PlantillaTipo>
              options={[
                { value: 'checklist', label: 'Checklist', icon: <CheckSquare size={12} weight="bold" /> },
                { value: 'revision', label: 'Revisión', icon: <ShieldCheck size={12} weight="bold" /> },
                { value: 'ambas', label: 'Ambas', icon: <Stack size={12} weight="bold" /> },
              ]}
              value={tipo}
              onChange={setTipo}
              className="w-full"
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={activo}
            onChange={e => setActivo(e.target.checked)}
            className="w-4 h-4 cursor-pointer accent-brand-green-100"
          />
          <span>Activa (visible en el menú "Cargar plantilla")</span>
        </label>

        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={esPredeterminada}
            onChange={e => setEsPredeterminada(e.target.checked)}
            className="w-4 h-4 mt-0.5 cursor-pointer accent-brand-yellow"
          />
          <span className="flex flex-col">
            <span className="flex items-center gap-1.5">
              <Star size={13} weight="fill" className="text-brand-yellow" />
              Predeterminada
            </span>
            <span className="text-[11px] text-stone-500">
              Se carga automáticamente al crear una tarea de esta categoría
              {tipo === 'ambas' ? ' (checklist y revisión)' : tipo === 'revision' ? ' (revisión)' : ' (checklist)'}.
              Solo puede haber una por categoría y tipo.
            </span>
          </span>
        </label>

        <div className="flex justify-between items-center pt-2 border-t border-stone-100">
          <div className="flex items-center gap-2">
            <button
              onClick={handleDelete}
              disabled={deletePlantilla.isPending}
              className="text-xs px-3 py-1.5 border border-red-300 text-red-700 rounded hover:bg-red-50 flex items-center gap-1.5 disabled:opacity-50"
            >
              <Trash size={12} weight="bold" />
              Eliminar plantilla
            </button>
            <button
              onClick={handleDuplicate}
              disabled={duplicatePlantilla.isPending}
              className="text-xs px-3 py-1.5 border border-stone-300 text-stone-700 rounded hover:bg-stone-50 flex items-center gap-1.5 disabled:opacity-50"
              title="Crea una copia con el mismo nombre + (copia) y los mismos items"
            >
              <Copy size={12} weight="bold" />
              {duplicatePlantilla.isPending ? 'Duplicando…' : 'Duplicar'}
            </button>
          </div>

          {headerDirty ? (
            <button
              onClick={saveHeader}
              disabled={updatePlantilla.isPending || !nombre.trim() || idCategoria === null}
              className="text-xs px-3 py-1.5 bg-brand-green-100 text-black font-semibold rounded hover:bg-brand-green-200 flex items-center gap-1.5 disabled:opacity-50"
            >
              <FloppyDisk size={12} weight="bold" />
              Guardar cambios
            </button>
          ) : (
            <span className="text-[11px] text-stone-400">Cambios se guardan al perder foco</span>
          )}
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-lg border border-stone-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-stone-700">
            Items <span className="text-stone-400 font-normal">({localItems.length})</span>
          </h3>
          <button
            onClick={handleAddItem}
            className="text-xs flex items-center gap-1 text-brand-gray-400 hover:text-brand-gray-500"
          >
            <Plus size={12} weight="bold" /> Agregar item
          </button>
        </div>

        {localItems.length === 0 ? (
          <p className="text-sm text-stone-500 italic">Sin items todavía.</p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localItems.map(i => i.idPlantillaTareaItem)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="space-y-2">
                {localItems.map(item => (
                  <PlantillaItemRow
                    key={item.idPlantillaTareaItem}
                    plantillaId={plantilla.idPlantillaTarea}
                    item={item}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}

// ============================================================
// PlantillaItemRow - una fila de item con edit inline + borrar.
// Click en el texto entra en modo edicion; blur o Enter persiste.
// ============================================================
function PlantillaItemRow({
  plantillaId,
  item,
}: {
  plantillaId: number;
  item: PlantillaTareaItem;
}) {
  const updateItem = useUpdatePlantillaItem();
  const deleteItem = useDeletePlantillaItem();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.texto);

  // dnd-kit sortable: setNodeRef en el li, attributes+listeners en el handle.
  // Asi el drag se inicia solo desde el handle y no interfiere con el click
  // del texto (modo edicion).
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: item.idPlantillaTareaItem });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  useEffect(() => { setText(item.texto); }, [item.texto]);

  function commit() {
    const next = text.trim();
    setEditing(false);
    if (!next || next === item.texto) {
      setText(item.texto);
      return;
    }
    updateItem.mutate({
      plantillaId,
      itemId: item.idPlantillaTareaItem,
      dto: { texto: next },
    });
  }

  async function handleDelete() {
    if (!window.confirm('¿Eliminar este item?')) return;
    await deleteItem.mutateAsync({
      plantillaId,
      itemId: item.idPlantillaTareaItem,
    });
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 group rounded ${isDragging ? 'bg-stone-100' : ''}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="opacity-0 group-hover:opacity-100 text-stone-300 hover:text-stone-600 cursor-grab active:cursor-grabbing transition-opacity p-0.5 flex-shrink-0"
        title="Arrastrar para reordenar"
        aria-label="Arrastrar para reordenar"
      >
        <DotsSixVertical size={14} weight="bold" />
      </button>
      <span className="text-stone-400 text-xs font-mono w-6 flex-shrink-0">
        {item.orden + 1}.
      </span>
      {editing ? (
        <input
          autoFocus
          value={text}
          onChange={e => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') { setText(item.texto); setEditing(false); }
          }}
          className="flex-1 bg-stone-100 border border-brand-green-100 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green-100"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="flex-1 text-left text-sm px-2 py-1 rounded hover:bg-stone-100 transition-colors"
        >
          {item.texto}
        </button>
      )}
      <button
        type="button"
        onClick={handleDelete}
        className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-600 transition-opacity p-1 rounded hover:bg-stone-100 flex-shrink-0"
        title="Eliminar item"
        aria-label="Eliminar item"
      >
        <X size={12} weight="bold" />
      </button>
    </li>
  );
}

// ============================================================
// NewPlantillaDialog - modal compacto para crear plantilla.
// Despues de crear navega al detalle de la nueva plantilla.
// ============================================================
function NewPlantillaDialog({
  defaultCategoryId,
  defaultTipo,
  onClose,
  onCreated,
}: {
  defaultCategoryId: number | null;
  defaultTipo: PlantillaTipo;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const { data: catalogs } = useCatalogs();
  const createPlantilla = useCreatePlantilla();
  const [nombre, setNombre] = useState('');
  const [idCategoria, setIdCategoria] = useState<number | null>(defaultCategoryId);
  const [tipo, setTipo] = useState<PlantillaTipo>(defaultTipo);
  const [esPredeterminada, setEsPredeterminada] = useState(false);

  const canSubmit = nombre.trim().length > 0 && idCategoria !== null;

  async function handleSubmit() {
    if (!canSubmit) return;
    const created = await createPlantilla.mutateAsync({
      nombre: nombre.trim(),
      idCategoria: idCategoria!,
      tipo,
      esPredeterminada,
    });
    onCreated(created.idPlantillaTarea);
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-md p-5 space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Nueva plantilla</h2>
          <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Nombre</label>
          <input
            autoFocus
            value={nombre}
            onChange={e => setNombre(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && canSubmit) handleSubmit(); }}
            placeholder="Ej. Permisos Construcción"
            className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Categoría</label>
          <SearchableSelect<number>
            options={(catalogs?.categories ?? []).map(c => ({
              value: c.idCatTareaArq,
              label: c.nombre || `#${c.idCatTareaArq}`,
            }))}
            value={idCategoria}
            onChange={setIdCategoria}
            placeholder="— Seleccionar —"
            clearable={false}
          />
        </div>

        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Tipo</label>
          <SegmentedControl<PlantillaTipo>
            options={[
              { value: 'checklist', label: 'Checklist', icon: <CheckSquare size={12} weight="bold" /> },
              { value: 'revision', label: 'Revisión', icon: <ShieldCheck size={12} weight="bold" /> },
              { value: 'ambas', label: 'Ambas', icon: <Stack size={12} weight="bold" /> },
            ]}
            value={tipo}
            onChange={setTipo}
            className="w-full"
          />
        </div>

        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={esPredeterminada}
            onChange={e => setEsPredeterminada(e.target.checked)}
            className="w-4 h-4 mt-0.5 cursor-pointer accent-brand-yellow"
          />
          <span className="flex items-center gap-1.5">
            <Star size={13} weight="fill" className="text-brand-yellow" />
            Predeterminada (se carga al crear tareas de esta categoría)
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm border border-stone-300 rounded hover:bg-stone-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || createPlantilla.isPending}
            className="px-4 py-1.5 text-sm bg-brand-green-100 text-black font-semibold rounded hover:bg-brand-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createPlantilla.isPending ? 'Creando…' : 'Crear'}
          </button>
        </div>
      </div>
    </div>
  );
}
